import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Shield,
  FileText,
  CheckCircle,
  Building2,
  Globe,
  Award,
  ChevronRight,
  Download,
} from "lucide-react";

const GovernmentContractingPage = () => {
  const complianceItems = [
    {
      icon: Shield,
      title: "Veteran-Owned Small Business",
      description: "Certified veteran-owned business eligible for VOSB set-aside contracts.",
    },
    {
      icon: Globe,
      title: "SAM.gov Registered",
      description: "Active registration in the System for Award Management for federal contracting.",
    },
    {
      icon: Building2,
      title: "HUBZone Aligned",
      description: "Operations aligned with Historically Underutilized Business Zones.",
    },
    {
      icon: FileText,
      title: "CAGE Code Active",
      description: "Active Commercial and Government Entity code for defense contracting.",
    },
    {
      icon: Award,
      title: "UEI Active",
      description: "Active Unique Entity Identifier for government transactions.",
    },
    {
      icon: CheckCircle,
      title: "Fully Insured",
      description: "Comprehensive insurance coverage meeting government contractor requirements.",
    },
  ];

  const targetClients = [
    "Department of Defense (DoD)",
    "Federal Agencies",
    "Prime Contractors",
    "State & Local Governments",
    "Airport Authorities",
    "Aviation Companies",
    "Infrastructure & Energy Firms",
  ];

  const coreCompetencies = [
    "Rapid-response air cargo logistics",
    "Government expeditionary support (WEXMAC 2.0 compliant)",
    "Aircraft charter services (civilian and federal)",
    "ISR (Intelligence, Surveillance, Reconnaissance) flight operations",
    "Veteran-operated aviation support",
  ];

  return (
    <div className="min-h-screen" data-testid="government-contracting-page">
      {/* Hero Section */}
      <section
        className="relative py-20 bg-[#1a365d]"
        data-testid="gov-hero"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">
                FEDERAL CONTRACTING
              </p>
              <h1 className="font-heading text-4xl md:text-6xl text-white mb-6">
                GOVERNMENT CONTRACTOR READY
              </h1>
              <p className="text-slate-200 text-lg leading-relaxed mb-8">
                AMR Global Holdings LLC maintains full compliance for federal, state, and 
                municipal contracting. Our veteran-owned business is positioned to support 
                government agencies and prime contractors with aviation and logistics services.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact?type=capability_statement">
                  <Button className="btn-tactical bg-[#c5a04a] text-[#1a365d] hover:bg-[#a38332] text-sm" data-testid="gov-capability-btn">
                    <Download className="w-4 h-4 mr-2" />
                    Request Capability Statement
                  </Button>
                </Link>
                <Link to="/contact?type=contracting">
                  <Button variant="outline" className="btn-tactical border-2 border-white text-white hover:bg-white hover:text-[#1a365d] text-sm" data-testid="gov-inquiry-btn">
                    Contracting Inquiry
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-sm border border-white/20">
                <img
                  src="https://customer-assets.emergentagent.com/job_rapid-deploy-52/artifacts/ftkxkyhf_1000028202.jpg"
                  alt="AMR Global Holdings Capability Statement"
                  className="rounded-sm shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance & Certifications */}
      <section className="py-20 bg-white" data-testid="compliance-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">COMPLIANCE</p>
            <h2 className="font-heading text-3xl md:text-5xl text-[#1a365d]">
              CERTIFICATIONS & STATUS
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {complianceItems.map((item, index) => (
              <Card key={index} className="card-tactical" data-testid={`compliance-card-${index}`}>
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-[#1a365d] rounded-sm flex items-center justify-center mb-6">
                    <item.icon className="w-7 h-7 text-[#c5a04a]" />
                  </div>
                  <h3 className="font-heading text-xl text-[#1a365d] mb-3">{item.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Core Competencies */}
      <section className="py-20 bg-slate-50 border-y border-slate-200" data-testid="competencies-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">CAPABILITIES</p>
              <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d] mb-6">
                CORE COMPETENCIES
              </h2>
              <ul className="space-y-4">
                {coreCompetencies.map((competency, index) => (
                  <li key={index} className="flex items-start gap-3" data-testid={`competency-${index}`}>
                    <CheckCircle className="w-5 h-5 text-[#c5a04a] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600">{competency}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">DIFFERENTIATORS</p>
              <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d] mb-6">
                WHY PARTNER WITH US
              </h2>
              <ul className="space-y-4">
                {[
                  "100% Veteran-pilot workforce",
                  "Rapid deployment anywhere CONUS/OCONUS",
                  "Mission-alignment with DOD & DHS needs",
                  "Airborne logistics expertise for critical and last mile delivery",
                  "72-hour mobilization capability",
                ].map((diff, index) => (
                  <li key={index} className="flex items-start gap-3" data-testid={`differentiator-${index}`}>
                    <Shield className="w-5 h-5 text-[#1a365d] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600">{diff}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* NAICS Codes */}
      <section className="py-20 bg-[#1a365d]" data-testid="naics-codes-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">CODES</p>
            <h2 className="font-heading text-3xl md:text-4xl text-white">NAICS CODES</h2>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { code: "481112", description: "Scheduled Freight Air Transportation" },
              { code: "481211", description: "Nonscheduled Chartered Freight Air Transportation" },
              { code: "489510", description: "Other Support Activities for Air Transportation" },
              { code: "541614", description: "Process, Physical Distribution, and Logistics Consulting" },
              { code: "561210", description: "Facilities Support Services" },
            ].map((naics, index) => (
              <div
                key={index}
                className="p-6 border border-slate-700 rounded-sm text-center hover:border-[#c5a04a] transition-colors"
                data-testid={`naics-${index}`}
              >
                <p className="font-mono text-3xl text-[#c5a04a] mb-2">{naics.code}</p>
                <p className="text-slate-300 text-xs leading-relaxed">{naics.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Clients */}
      <section className="py-20 bg-white" data-testid="target-clients-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">CLIENTS</p>
            <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d]">WHO WE SERVE</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {targetClients.map((client, index) => (
              <span
                key={index}
                className="px-6 py-3 bg-slate-100 text-[#1a365d] font-heading text-sm tracking-wider rounded-sm hover:bg-[#1a365d] hover:text-white transition-colors cursor-default"
                data-testid={`client-${index}`}
              >
                {client}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-20 relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1767491629923-009ffdc395d8?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="gov-cta"
      >
        <div className="absolute inset-0 bg-[#1a365d]/90"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
            READY FOR FEDERAL CONTRACTING
          </h2>
          <p className="text-slate-200 text-lg mb-8">
            Request our capability statement or submit a contracting inquiry. 
            AMR Global Holdings LLC is positioned to support your government contract requirements.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact?type=capability_statement">
              <Button className="btn-tactical bg-[#c5a04a] text-[#1a365d] hover:bg-[#a38332] text-sm" data-testid="cta-capability-btn">
                <FileText className="w-4 h-4 mr-2" />
                Request Capability Statement
              </Button>
            </Link>
            <Link to="/contact?type=partnership">
              <Button variant="outline" className="btn-tactical border-2 border-white text-white hover:bg-white hover:text-[#1a365d] text-sm" data-testid="cta-partner-btn">
                Partner With Us
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GovernmentContractingPage;
